import { useState, useEffect, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import { User, Calendar, DollarSign, Stethoscope, ChevronRight } from 'lucide-react'
import { searchService, SearchResult } from '../services/searchService'

interface SearchDropdownProps {
  isOpen: boolean
  searchTerm: string
  onClose: () => void
  onResultClick: () => void
}

export default function SearchDropdown({ isOpen, searchTerm, onClose, onResultClick }: SearchDropdownProps) {
  const navigate = useNavigate()
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Query para buscar resultados
  const { data: searchResponse, isLoading, error } = useQuery({
    queryKey: ['search', searchTerm],
    queryFn: () => searchService.searchAll(searchTerm, 5),
    enabled: isOpen && searchTerm.trim().length > 0,
    staleTime: 30000, // Cache por 30 segundos
    retry: 1, // Solo reintentar una vez
  })

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose()
      }
    }

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, onClose])

  // Manejar navegación a resultados específicos
  const handleResultClick = (result: SearchResult) => {
    onResultClick()
    
    switch (result.type) {
      case 'paciente':
        navigate(`/pacientes?search=${encodeURIComponent(result.title)}`)
        break
      case 'atencion':
        navigate(`/atenciones?search=${encodeURIComponent(result.title)}`)
        break
      case 'gasto':
        navigate(`/gastos?search=${encodeURIComponent(result.title)}`)
        break
      case 'tratamiento':
        navigate(`/tratamientos?search=${encodeURIComponent(result.title)}`)
        break
    }
  }

  // Obtener icono según el tipo
  const getIcon = (type: string) => {
    switch (type) {
      case 'paciente':
        return <User className="h-4 w-4 text-blue-500" />
      case 'atencion':
        return <Calendar className="h-4 w-4 text-green-500" />
      case 'gasto':
        return <DollarSign className="h-4 w-4 text-red-500" />
      case 'tratamiento':
        return <Stethoscope className="h-4 w-4 text-purple-500" />
      default:
        return <User className="h-4 w-4 text-gray-500" />
    }
  }

  // Obtener color de fondo según el tipo
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'paciente':
        return 'bg-blue-50 border-blue-200'
      case 'atencion':
        return 'bg-green-50 border-green-200'
      case 'gasto':
        return 'bg-red-50 border-red-200'
      case 'tratamiento':
        return 'bg-purple-50 border-purple-200'
      default:
        return 'bg-gray-50 border-gray-200'
    }
  }

  // Obtener etiqueta del tipo
  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'paciente':
        return 'Paciente'
      case 'atencion':
        return 'Atención'
      case 'gasto':
        return 'Gasto'
      case 'tratamiento':
        return 'Tratamiento'
      default:
        return 'Resultado'
    }
  }

  if (!isOpen) return null

  // Manejo de errores
  if (error) {
    return (
      <div
        ref={dropdownRef}
        className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 z-50"
      >
        <div className="px-4 py-3 text-sm text-red-500 text-center">
          Error al buscar. Intenta de nuevo.
        </div>
      </div>
    )
  }

  const results = searchResponse ? searchService.formatSearchResults(searchResponse) : []

  return (
    <div
      ref={dropdownRef}
      className="absolute top-full left-0 right-0 mt-1 bg-white rounded-md shadow-lg border border-gray-200 z-50 max-h-96 overflow-y-auto"
    >
      {isLoading ? (
        <div className="px-4 py-3 text-sm text-gray-500 flex items-center justify-center">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500 mr-2"></div>
          Buscando...
        </div>
      ) : results.length > 0 ? (
        <div className="py-1">
          {/* Header con total de resultados */}
          <div className="px-4 py-2 border-b border-gray-200 bg-gray-50">
            <p className="text-xs font-medium text-gray-600">
              {results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Lista de resultados */}
          {results.map((result) => (
            <button
              key={`${result.type}-${result.id}`}
              onClick={() => handleResultClick(result)}
              className="w-full text-left px-4 py-3 hover:bg-gray-50 focus:outline-hidden focus:bg-gray-50 border-b border-gray-100 last:border-b-0"
            >
              <div className="flex items-start space-x-3">
                {/* Icono */}
                <div className="shrink-0 mt-0.5">
                  {getIcon(result.type)}
                </div>

                {/* Contenido */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {result.title}
                      </p>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTypeColor(result.type)}`}>
                        {getTypeLabel(result.type)}
                      </span>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                  </div>
                  
                  {result.subtitle && (
                    <p className="text-xs text-gray-600 mt-1 truncate">
                      {result.subtitle}
                    </p>
                  )}
                  
                  {result.description && (
                    <p className="text-xs text-gray-500 mt-1 line-clamp-2">
                      {result.description}
                    </p>
                  )}
                </div>
              </div>
            </button>
          ))}

          {/* Footer con enlace para ver más resultados */}
          {searchResponse && searchResponse.total > results.length && (
            <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
              <button
                onClick={() => {
                  onResultClick()
                  navigate(`/pacientes?search=${encodeURIComponent(searchTerm)}`)
                }}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                Ver todos los resultados ({searchResponse.total})
              </button>
            </div>
          )}
        </div>
      ) : searchTerm.trim().length > 0 ? (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">
          No se encontraron resultados para "{searchTerm}"
        </div>
      ) : (
        <div className="px-4 py-3 text-sm text-gray-500 text-center">
          Escribe algo para buscar...
        </div>
      )}
    </div>
  )
}
